import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ActiveState } from "@common/ADAPT.Common.Model/activeEntity.interface";
import { HandoverStatusMetadata } from "@common/ADAPT.Common.Model/organisation/handover-status";
import { Role, RoleBreezeModel } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { DirectorySharedService } from "@org-common/lib/directory-shared/directory-shared.service";
import moment from "moment";
import { Subject } from "rxjs";

@Component({
    selector: "adapt-edit-role-connection",
    templateUrl: "./edit-role-connection.component.html",
    styleUrl: "./edit-role-connection.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EditRoleConnectionComponent extends BaseComponent implements OnInit {
    @Output() public roleConnectionChanged = new EventEmitter<RoleConnection>();
    @Output() public roleConnectionDeleted = new EventEmitter<RoleConnection>();

    @Input() public roleConnection?: RoleConnection;
    @Input() public model = RoleBreezeModel;
    @Input() public minStartDate?: Date;
    @Input() public isAdding = false;

    public readonly HandoverStatuses = HandoverStatusMetadata.CurrentRoleHandoverOptions;
    public readonly HandoverByStatus = HandoverStatusMetadata.ByStatus;

    public endConnectionDisableMessage = "";
    public canEndConnection = false;
    public showEndDate = false;
    public maxStartDate?: Date;
    public endDateRequired = false;
    public maxEndDate?: Date;
    public endDateOpened = false;

    private roleConnectionChange$ = new Subject<void>();

    constructor(
        private directorySharedService: DirectorySharedService,
    ) {
        super();
    }

    public async ngOnInit() {
        await this.updateSettingsFromOtherRoleConnections(); // set initial value for canReactivate

        if (this.roleConnection?.connection) {
            this.minStartDate = this.roleConnection.connection.startDate;
        }

        if (this.minStartDate && this.roleConnection && moment(this.minStartDate).isAfter(this.roleConnection?.startDate)) {
            // startDate is before minStartDate (usually the connection startDate)
            this.roleConnection.startDate = this.minStartDate;
        }

        if (this.roleConnection!.endDate) {
            this.maxStartDate = this.roleConnection!.endDate;

            // trigger display of endDate separately from actual endDate value so that
            // dxDateBox is not unloaded before onValueChanged can be run.
            this.showEndDate = true;
        }

        if (this.roleConnection!.isHistoric()) {
            this.endDateRequired = true;
        }

        this.roleConnectionChange$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateSettingsFromOtherRoleConnections());
    }

    private async updateSettingsFromOtherRoleConnections() {
        if (this.roleConnection!.connection) {
            const today = moment.utc();
            const matchingActiveRoleConnection = this.getMatchingActiveRoleConnection();

            const canRemove = await this.directorySharedService.promiseToVerifyRoleRemoval(this.roleConnection!.role, this.roleConnection!.connection?.person);
            this.updateCanEndConnection(canRemove);

            if (matchingActiveRoleConnection) {
                this.maxEndDate = moment.min(moment(matchingActiveRoleConnection.startDate), today).toDate();
            } else if (!this.roleConnection!.role || this.roleConnection!.role.isActive()) {
                this.maxEndDate = today.toDate();
            }
        }
    }

    private updateCanEndConnection(canRemove: boolean) {
        this.canEndConnection = canRemove;
        if (canRemove) {
            this.endConnectionDisableMessage = "";
        } else {
            this.endConnectionDisableMessage = "Role to edit access management cannot be ended by the current person";
        }
    }

    public get minEndDate() {
        return this.roleConnection?.startDate;
    }

    public get startDateInFuture() {
        return this.roleConnection?.activeState === ActiveState.PendingActive;
    }

    public onChanged() {
        this.roleConnectionChanged.emit(this.roleConnection!);
        this.roleConnectionChange$.next();
    }

    private getMatchingActiveRoleConnection() {
        const matchingRoleConnections = this.roleConnection!.connection?.roleConnections
            .filter((i) => this.matchRoleConnection(i));

        return ArrayUtilities.getSingleFromArray(matchingRoleConnections);
    }

    private matchRoleConnection(roleConnection: RoleConnection) {
        return roleConnection.roleConnectionId !== this.roleConnection!.roleConnectionId
            && roleConnection.roleId === this.roleConnection!.roleId
            && roleConnection.connectionId === this.roleConnection!.connectionId
            && roleConnection.teamId === this.roleConnection!.teamId
            && roleConnection.isNotHistoric();
    }

    @Autobind
    public roleFilter(role?: Role) {
        const isRoleAllocated = (roleConnection: RoleConnection) =>
            roleConnection.isNotHistoric()
            && roleConnection.role === role
            && roleConnection.teamId === this.roleConnection!.teamId;

        const isCurrentlyAllocatedRole = () =>
            this.roleConnection!.connection?.roleConnections.some(isRoleAllocated);

        const allocatingExistingRoleToTeam = () =>
            this.roleConnection!.team && isCurrentlyAllocatedRole();

        const isAllowedSystemRole = () =>
            !!role && (!role.extensions.isSystemAllocatedRole() || allocatingExistingRoleToTeam());

        return !isCurrentlyAllocatedRole()
            && isAllowedSystemRole();
    }

    @Autobind
    public connectionFilter(person?: Person) {
        const isConnectionAssignedToRole = (roleConnection: RoleConnection) => {
            return roleConnection.connection
                && roleConnection.connection.person === person
                && roleConnection.teamId === this.roleConnection!.teamId
                && roleConnection.isNotHistoric();
        };

        return !this.roleConnection!.role?.roleConnections.some(isConnectionAssignedToRole);
    }

    public onStartDateChanged(value: Date) {
        this.roleConnection!.startDate = value;

        this.onChanged();
    }

    public onEndDateChanged(value: Date) {
        if (value) {
            // startDate cannot be after endDate
            this.maxStartDate = value;
            this.roleConnection!.endDate = value;
        } else {
            // when the date has been cleared using the clear button, we must trigger an OnChanged() event
            this.roleConnection!.endDate = undefined;
        }

        this.onChanged();

        this.showEndDate = !!value;
    }

    public onHandoverStatusChange(value: HandoverStatusMetadata) {
        this.roleConnection!.handoverStatus = value.status ?? undefined;
        this.onChanged();
    }

    public onPersonChanged(person?: Person) {
        if (!person) {
            return;
        }
        this.roleConnection!.connection = person.getLatestConnection()!;
        this.minStartDate = this.roleConnection!.connection.startDate;
        if (this.roleConnection!.startDate < this.minStartDate) {
            this.roleConnection!.startDate = this.minStartDate;
        }

        this.onChanged();
    }

    public promptToDeleteRoleConnection() {
        this.directorySharedService.promptToRemoveRoleConnection(this.roleConnection!).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.onDeletion());
    }

    public endRoleConnection(endDatePickerDelay = 0) {
        // make sure the end date is not before the start date
        this.roleConnection!.endDate = moment.max(moment.utc(), moment(this.roleConnection!.startDate)).toDate();
        this.onChanged();

        this.showEndDate = true;

        // do in next digest so the moving date box doesn't cause popup to be in odd position
        setTimeout(() => this.endDateOpened = true, endDatePickerDelay);
    }

    public onDeletion() {
        this.roleConnectionDeleted.emit(this.roleConnection!);
        this.roleConnectionChange$.next();
    }
}
